import {isString} from 'lodash';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {electronAppVersionGTE} from '../../../libs/shared/helpers/src/environment/platformHelpers';
import {reportToBugsnag} from '../../../srcCommon/services/bugsnag/bugsnag';
import {delayAsync} from '../../helpers/asyncHelpers';
import {AppState} from '../../store/appState';
import {AppStore} from '../../store/appStore';
import {getCompany} from '../../store/data/company/companySelectors';
import {getUser} from '../../store/data/teammates/teammatesSelectors';
import {electronInitializeAsyncAction} from '../../store/electron/electronActions';
import {electron} from './electron';
import {setupElectronBridgeInbound} from './electronBridgeInbound';
import {tryNavigateToLinkAsync} from './electronBridgeNavigateToLink';
import {setupElectronBridgeOutbound} from './electronBridgeOutbound';

const windowBridge = globalWindow.parent && (globalWindow.parent as any).windowBridge;
const localBridge = globalWindow.parent && (globalWindow.parent as any).localBridge;

export function maybeSetupElectronBridge(store: AppStore) {
  if (!electron || !windowBridge || !localBridge) {
    return;
  }

  /*
   * Electron => Front.
   */

  setupElectronBridgeInbound(store.dispatch, electron);

  /*
   * Front => Electron.
   */

  setupElectronBridgeOutbound(store, windowBridge, localBridge);

  // Initialization.
  const initialState = store.getState();

  setFeatureFlagsAsync(initialState);
  notifyAppLoadedAsync(initialState);
  checkForPendingMailToAsync();

  store.dispatch(electronInitializeAsyncAction());
}

/*
 * Post-boot.
 */

async function notifyAppLoadedAsync(state: AppState) {
  try {
    const company = getCompany(state);
    const user = getUser(state);

    await delayAsync();
    localBridge.appLoaded(company.id, user.id);
  } catch (error) {
    reportToBugsnag(error);
  }
}

async function checkForPendingMailToAsync() {
  if (!electronAppVersionGTE('3.9.13')) {
    return;
  }

  try {
    const pendingMailTo = await windowBridge.pendingMailTo();
    if (!pendingMailTo || !isString(pendingMailTo)) {
      return;
    }

    await tryNavigateToLinkAsync(pendingMailTo);
  } catch (error) {
    reportToBugsnag(error);
  }
}

async function setFeatureFlagsAsync(state: AppState) {
  if (!electronAppVersionGTE('3.11.0')) {
    return;
  }

  try {
    if (!state.data.config.launchdarkly.isEnabled) {
      return;
    }

    await windowBridge.setFeatureFlags(state.data.config.launchdarkly.flags);
  } catch (error) {
    reportToBugsnag(error);
  }
}

/*
 * Helpers.
 */

export function shouldShowNotificationsUsingElectron() {
  return electronAppVersionGTE('3.12.0');
}
